// @fileoverview : 경남-PMS > 고속성형기관리 > 에러보기
import { systemAccessLogHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { CommonCodeCreateForm } from "@/features/commonCode/form/CreateForm";
import { CommonPartCodeCreateForm } from "@/features/commonCode/form/PartCreateForm";
import { CommonPartCodeUpdateForm } from "@/features/commonCode/form/PartUpdateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { ErrorViewUpdateForm } from "@/features/PMS/press/erroview/form/updateForm";
import { ActionHeader } from "@/features/standard/ActionHeader";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import customAlert from "@/features/ui/alert/Alert";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import useQueryString from "@/hooks/useQueryString";
import styled from "@emotion/styled";
import { Button, Checkbox, Flex, Pagination } from "@mantine/core";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";
import { useTranslation } from "react-i18next";

const ErrorWrap = {
  Header: Header,
  HeaderTitle: HeaderTitle,
  HeaderSubTitle: HeaderSubTitle,
  ActionHeader: ActionHeader,
  ActionButton: Button,
  Wrapper: Wrapper,
  CreateForm: CommonCodeCreateForm,
  UpdateForm: ErrorViewUpdateForm,
  PartCreateForm: CommonPartCodeCreateForm,
  PartUpdateForm: CommonPartCodeUpdateForm,
};

export const PressErrorViewPage = () => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);
  const { t } = useTranslation();
  const { openModal } = useModal();
  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });
  const [parentActivePage, setParentActivePage] = useState(1); // 좌측 테이블의 페이지 상태
  const [childActivePage, setChildActivePage] = useState(1); // 우측 테이블의 페이지 상태

  const searchFieldsHeader = systemAccessLogHeader.filter(
    (item) => item.category === "text" && !item.isEnum,
  );

  const pageSize = 10;
  // 좌측 데이터의 총 갯수 및 페이지 계산
  const totalParentRows = ErrorMockupData.length;
  const parentPaginatedRows = ErrorMockupData?.slice(
    (parentActivePage - 1) * pageSize,
    parentActivePage * pageSize,
  );

  // 우측 데이터의 총 갯수 및 페이지 계산
  const totalChildRows = MachineMockupData.length;
  const childPaginatedRows = MachineMockupData.slice(
    (childActivePage - 1) * pageSize,
    childActivePage * pageSize,
  );
  const parentColumns: readonly Column<any>[] = [
    {
      key: "machineCode",
      name: "기계제조사",
      sortable: true,
      resizable: true,
    },
    {
      key: "machineName",
      name: "기계이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "제조 연월일",
      sortable: true,
      resizable: true,
    },
    {
      key: "id",
      name: "제조번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <ErrorWrap.UpdateForm formatterProps={formatterProps.row} />,
                  {},
                  "공통코드 수정",
                  true,
                );
              }}
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                window.confirm("정말로 삭제하시겠습니까?") &&
                  customAlert("공통코드 삭제에 실패하였습니다.", "삭제 실패", "red");
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  const childColumns: readonly Column<any>[] = [
    {
      key: "errorId",
      name: "에러번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorName",
      name: "명칭",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorUse",
      name: "알림",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <>
      <ErrorWrap.Wrapper>
        <ErrorWrap.Header>
          <ErrorWrap.HeaderTitle>{"에러보기"}</ErrorWrap.HeaderTitle>
          <ErrorWrap.HeaderSubTitle>
            {"우리 회사에서 다루는 에러에 대해 조회합니다."}
          </ErrorWrap.HeaderSubTitle>
        </ErrorWrap.Header>
        <Flex w="100%" justify="space-between">
          <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
            <CustomFilter filterType={systemAccessLogHeader} setQuery={setQuery} query={query} />
            <CustomSorter sorterType={systemAccessLogHeader} setSort={setSort} sort={sort} />
          </Flex>
        </Flex>

        <Flex w="100%" h="100%" gap="sm">
          {/* 왼쪽 공통코드 테이블 */}
          <GridWrapper>
            <DataGrid
              columns={parentColumns ?? []}
              rows={parentPaginatedRows ?? []}
              rowHeight={40}
              rowKeyGetter={(row) => row.commonCode}
            />
            <Pagination
              onChange={setParentActivePage} // 페이지 변경 핸들러
              value={parentActivePage}
              total={Math.ceil(totalParentRows / pageSize)} // 데이터에 따라 동적 계산
              size="lg"
              radius="sm"
              position="center"
            />
          </GridWrapper>

          <GridWrapper>
            <Flex direction="column" gap="sm" p="md">
              <DataGrid
                columns={childColumns ?? []}
                rows={childPaginatedRows ?? []} // 선택된 코드의 detail 데이터
                rowHeight={40}
                rowKeyGetter={(row) => row.categoryCode}
              />
              <Pagination
                onChange={setChildActivePage} // 우측 페이지네이션 핸들러
                value={childActivePage}
                total={Math.ceil(totalChildRows / pageSize)} // 데이터에 따라 동적 계산
                size="lg"
                radius="sm"
                position="center"
              />
            </Flex>
          </GridWrapper>
        </Flex>
      </ErrorWrap.Wrapper>
    </>
  );
};

const ErrorMockupData = [
  {
    machineCode: "화일프레스",
    machineName: "10호기 160T",
    createdAt: "2025-06-01",
    id: "001",
  },
  {
    machineCode: "SIMPAC",
    machineName: "23호기 400T",
    createdAt: "2025-06-01",
    id: "002",
  },
  {
    machineCode: "화일프레스",
    machineName: "5호기 200T",
    createdAt: "2025-06-03",
    id: "003",
  },
  {
    machineCode: "화일프레스",
    machineName: "7호기 160T",
    createdAt: "2025-06-03",
    id: "004",
  },
  {
    machineCode: "화일프레스",
    machineName: "8호기 160T",
    createdAt: "2025-06-13",
    id: "005",
  },
  {
    machineCode: "SIMPAC",
    machineName: "6호기 200T",
    createdAt: "2025-06-03",
    id: "006",
  },
  {
    machineCode: "화일프레스",
    machineName: "9호기 160T",
    createdAt: "2025-06-04",
    id: "007",
  },
  {
    machineCode: "화일프레스",
    machineName: "11호기 200T",
    createdAt: "2025-06-15",
    id: "008",
  },
  {
    machineCode: "SIMPAC",
    machineName: "4호기 600T",
    createdAt: "2025-06-13",
    id: "009",
  },
  {
    machineCode: "서울프레스",
    machineName: "1호기 250T",
    createdAt: "2025-06-03",
    id: "010",
  },
  {
    machineCode: "서울프레스",
    machineName: "2호기 250T",
    createdAt: "2025-06-13",
    id: "011",
  },
  {
    machineCode: "서울프레스",
    machineName: "3호기 250T",
    createdAt: "2025-06-14",
    id: "012",
  },
  {
    machineCode: "서울프레스",
    machineName: "4호기 250T",
    createdAt: "2025-06-15",
    id: "013",
  },
];

const MachineMockupData = [
  {
    errorId: "1",
    errorName: "01-키캠 접점이상",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "2",
    errorName: "02-프로세스 이상",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "3",
    errorName: "03-사용안함",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "4",
    errorName: "04-사용안함",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "5",
    errorName: "05-사용안함",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "6",
    errorName: "06-프로세스 이상",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "7",
    errorName: "07-조작반 비상정지",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "8",
    errorName: "08-조작반 비상정지",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "9",
    errorName: "09-안정기 캠 미설정",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "10",
    errorName: "10-상사점 캠 미설정",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "11",
    errorName: "11-오버런 캠 미설정",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "12",
    errorName: "12-안전일핼정 캠 미설정",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "13",
    errorName: "13-안전센서 접점 이상",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "14",
    errorName: "14-운전중 키캠변경",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "15",
    errorName: "15-프리셋 카운터 생산 완료",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "16",
    errorName: "16-SPM 설정 속도 초과",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "17",
    errorName: "17-사용안함",
    errorUse: <Checkbox checked />,
  },
  {
    errorId: "18",
    errorName: "18-운전 중 자동/수동 변경",
    errorUse: <Checkbox checked />,
  },
];

const GridWrapper = styled.div`
  & *[role="grid"] {
    height: 440px;
    // --rdg-background-color: {(props) => (props.isDarkMode ? "white" : "none")};
    // --rdg-header-background-color: {(props) =>
    // props.isDarkMode ? "white" : "none"};
    // --rdg-color: {(props) => (props.isDarkMode ? "black" : "white")};
    // --rdg-row-hover-background-color: {(props) =>
    // props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;

const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;

const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
